import { Cpu, MemoryStick, HardDrive, Users, Network } from 'lucide-react'
import type { DashboardCards } from '../../types/monitoring'
import { formatPercent, formatUptime } from './format'
import { thresholdStatus } from './StatusIndicator'

const CARD_THEME: Record<string, { chip: string; icon: string; bar: string }> = {
  blue: { chip: 'bg-blue-50', icon: 'text-blue-600', bar: 'bg-blue-500' },
  indigo: { chip: 'bg-indigo-50', icon: 'text-indigo-600', bar: 'bg-indigo-500' },
  purple: { chip: 'bg-purple-50', icon: 'text-purple-600', bar: 'bg-purple-500' },
  emerald: { chip: 'bg-emerald-50', icon: 'text-emerald-600', bar: 'bg-emerald-500' },
  sky: { chip: 'bg-sky-50', icon: 'text-sky-600', bar: 'bg-sky-500' },
  navy: { chip: 'bg-blue-50', icon: 'text-blue-800', bar: 'bg-blue-800' },
}

function Card({
  label, value, sub, icon, theme, loading, percentForStatus,
}: {
  label: string; value: string; sub?: string; icon: React.ReactNode; theme: keyof typeof CARD_THEME
  loading: boolean; percentForStatus?: number | null
}) {
  const status = percentForStatus !== undefined ? thresholdStatus(percentForStatus) : null
  const ringColor = status === 'critical' ? 'ring-red-200' : status === 'warn' ? 'ring-amber-200' : ''
  const t = CARD_THEME[theme]

  return (
    <div className={`relative bg-white rounded-2xl shadow-sm border border-neutral-200 p-5 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${ringColor ? `ring-2 ${ringColor}` : ''}`}>
      <div className={`absolute top-0 left-0 right-0 h-1 ${t.bar}`} />
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.chip}`}>
          <span className={t.icon}>{icon}</span>
        </div>
        {status === 'critical' && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse mt-1" />}
      </div>
      <p className="text-neutral-400 text-[11px] font-semibold uppercase tracking-wider mb-1.5">{label}</p>
      {loading ? (
        <div className="h-8 w-20 bg-neutral-100 rounded-md animate-pulse" />
      ) : (
        <div className="flex items-baseline gap-2">
          <p className="text-2xl font-bold text-blue-950 tabular-nums tracking-tight">{value}</p>
          {sub && <p className="text-xs text-neutral-400">{sub}</p>}
        </div>
      )}
    </div>
  )
}

export function KPICards({ cards, loading }: { cards: DashboardCards | undefined; loading: boolean }) {
  const items = [
    {
      label: 'CPU Usage',
      value: formatPercent(cards?.cpu_percent),
      sub: cards?.load_avg_1m != null ? `load ${cards.load_avg_1m.toFixed(2)}` : undefined,
      icon: <Cpu className="w-5 h-5" />,
      theme: 'blue' as const,
      percentForStatus: cards?.cpu_percent,
    },
    {
      label: 'Memory Usage',
      value: formatPercent(cards?.memory_percent),
      sub: cards?.swap_percent != null ? `swap ${cards.swap_percent.toFixed(0)}%` : undefined,
      icon: <MemoryStick className="w-5 h-5" />,
      theme: 'indigo' as const,
      percentForStatus: cards?.memory_percent,
    },
    {
      label: 'Disk Usage',
      value: formatPercent(cards?.disk_percent),
      icon: <HardDrive className="w-5 h-5" />,
      theme: 'purple' as const,
      percentForStatus: cards?.disk_percent,
    },
    {
      label: 'Uptime',
      value: formatUptime(cards?.uptime_seconds),
      sub: cards?.process_count != null ? `${cards.process_count} procs` : undefined,
      icon: <Users className="w-5 h-5" />,
      theme: 'emerald' as const,
    },
    {
      label: 'TCP Established',
      value: cards?.tcp_established?.toLocaleString() ?? '—',
      icon: <Network className="w-5 h-5" />,
      theme: 'navy' as const,
    },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
      {items.map((item) => (
        <Card key={item.label} {...item} loading={loading} />
      ))}
    </div>
  )
}


// import { useEffect, useRef } from 'react'

// function smoothScrollTo(targetY: number, duration = 900) {
//   const startY = window.scrollY
//   const distance = targetY - startY
//   const startTime = performance.now()

//   const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

//   function step(now: number) {
//     const elapsed = now - startTime
//     const progress = Math.min(elapsed / duration, 1)
//     window.scrollTo(0, startY + distance * easeInOutQuad(progress))
//     if (progress < 1) requestAnimationFrame(step)
//   }
//   requestAnimationFrame(step)
// }

// /**
//  * Auto-scrolls through every `[data-kiosk-section]` element inside
//  * `containerRef`, pausing `dwellMs` on each, then calls `onCycleEnd`
//  * once past the last one. Restarts cleanly whenever `resetKey` changes
//  * (pass the active tab so switching tabs doesn't leave a stale index).
//  */
// export function useKioskAutoplay(
//   containerRef: React.RefObject<HTMLElement>,
//   enabled: boolean,
//   dwellMs: number,
//   onCycleEnd: () => void,
//   resetKey: string,
// ) {
//   useEffect(() => {
//     if (!enabled) return
//     let index = 0
//     let cancelled = false
//     let timeoutId: number

//     const tick = () => {
//       if (cancelled) return
//       const container = containerRef.current
//       if (!container) return
//       const sections = Array.from(container.querySelectorAll<HTMLElement>('[data-kiosk-section]'))
//       if (!sections.length) return

//       if (index >= sections.length) {
//         onCycleEnd()
//         return
//       }
//       const el = sections[index]
//       const targetY = el.getBoundingClientRect().top + window.scrollY - 24
//       smoothScrollTo(targetY, 900)
//       index += 1
//       timeoutId = window.setTimeout(tick, dwellMs)
//     }

//     timeoutId = window.setTimeout(tick, dwellMs)
//     return () => {
//       cancelled = true
//       window.clearTimeout(timeoutId)
//     }
//   }, [enabled, dwellMs, onCycleEnd, containerRef, resetKey])
// }